/* eslint-disable import/prefer-default-export */
import * as React from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { ErrorResponse } from '@quality24/design-system';
import runtimeConfig from './config';

const environment = runtimeConfig.SENTRY_ENVIRONMENT || process.env.NODE_ENV;

const messageBlacklist = [
  `play() failed because the user didn't interact with the document first`,
  `The play() request was interrupted by a call to pause()`,
  `The request is not allowed by the user agent or the platform`,
];

export const handleOnComponentDidCatch = (error: ErrorResponse) => {
  try {
    if (Sentry?.captureException) {
      Sentry.captureException(error);
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Sentry is not available');
  }
};

Sentry.init({
  environment,
  dsn: runtimeConfig.SENTRY_DSN || process.env.REACT_APP_SENTRY_DSN,
  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
  beforeSend: (event) => {
    if (event?.user?.enableMonitoring === false) {
      return null;
    }

    if (messageBlacklist.some((message) => event.message?.includes(message))) {
      return null;
    }

    return {
      ...event,
      user: { id: event.user?.id, appVersion: process.env.REACT_APP_VERSION },
    };
  },
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration({ failedRequestStatusCodes: [[400, 599]] }),
  ],
  tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
  tracePropagationTargets: [
    'localhost',
    runtimeConfig.API_BASE_URL ||
      (process.env.REACT_APP_API_BASE_URL as string),
  ],
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
